import { inject, Injectable, signal } from '@angular/core'
import { MenuController, ModalController } from '@ionic/angular/standalone'

import { environment } from '../../../environments/environment'
import { CasinoDisabledModalComponent } from '../components/casino-disabled-modal/casino-disabled-modal.component'
import { navigateToUrl } from '../utils/navigation.util'
import { AuthService } from './auth.service'
import { BalanceService } from './balance.service'
import { DomainService } from './domain.service'
import { HelperService } from './helper.service'
import { WhiteLabelMarketConfigService } from './white-label-market-config.service'

export type DisplayMode = 'time' | 'league'

@Injectable({
	providedIn: 'root',
})
export class DisplayService {
	private readonly STORAGE_KEY = 'display_mode'

	private readonly DEFAULT_CASINO_CURRENCY = 'HKD'

	private readonly menuController = inject(MenuController)

	private readonly authService = inject(AuthService)

	private readonly helperService = inject(HelperService)

	private readonly domainService = inject(DomainService)

	private readonly wlMarketConfig = inject(WhiteLabelMarketConfigService)

	private readonly modalController = inject(ModalController)

	private readonly balanceService = inject(BalanceService)

	displayMode = signal<DisplayMode>('league')

	constructor() {
		const savedMode = localStorage.getItem(this.STORAGE_KEY) as DisplayMode
		if (savedMode && (savedMode === 'time' || savedMode === 'league')) {
			this.displayMode.set(savedMode)
		} else {
			// Set default to league if no saved preference
			localStorage.setItem(this.STORAGE_KEY, 'league')
		}
	}

	async setMode(mode: DisplayMode) {
		this.displayMode.set(mode)
		localStorage.setItem(this.STORAGE_KEY, mode)
		// Close the drawer when mode is changed
		await this.menuController.close()
	}

	get isCasinoBlocked(): boolean {
		return this.wlMarketConfig.showFeature('disableCasino') || this.authService.isCasinoBlocked$.getValue()
	}

	async openCasino(gameId?: string) {
		const isWlDisabled = this.wlMarketConfig.showFeature('disableCasino')
		const userData = this.helperService.getUserData()
		const isUserBlocked = userData?.user_fields?.IS_BLOCKED_CASINO === 'true'

		if (isWlDisabled || isUserBlocked) {
			const modal = await this.modalController.create({
				component: CasinoDisabledModalComponent,
				cssClass: 'casino-disabled-modal',
				breakpoints: [0, 1],
				initialBreakpoint: 1,
				handle: false,
				componentProps: {
					message: isUserBlocked ? 'Casino Blocked, contact your admin' : 'Casino Unavailable',
				},
			})
			await modal.present()
			return
		}

		if (!userData) {
			console.error('No user data available for casino')
			return
		}

		const casinoWallet = this.balanceService.casinoBalanceData$.getValue()
		const casinoCurrency = casinoWallet?.CURRENCY_CODE ?? userData.user_fields?.CURRENCY ?? this.DEFAULT_CASINO_CURRENCY
		const casinoUrl = this.getCasinoUrl(casinoCurrency, userData, gameId)

		navigateToUrl(casinoUrl)
	}

	private getCasinoUrl(casinoCurrency: string, userData: NonNullable<ReturnType<HelperService['getUserData']>>, gameId?: string): string {
		const base = environment.casinoUrl
		const logoutRedirectionUrl = this.domainService.hostname + '/home'
		const env = environment.environment?.toLowerCase()
		const userFields = userData.user_fields
		const userToken = this.authService.getAccessToken()
		const { ID, NAME } = userFields ?? {}

		const qs = new URLSearchParams({
			token: userToken,
			tokenTimestamp: String(Date.now()),
			userFields: JSON.stringify(userFields),
			userId: ID,
			currency: casinoCurrency,
			displayName: NAME,
			lang: this.helperService.getSelectedLanguage(),
			logoutRedirectionUrl,
			environment: env,
			origin: 'exchange',
		})

		if (gameId) {
			qs.set('gameId', gameId)
		}

		return `${base}?${qs.toString()}`
	}
}
